

// // src/pages/PatientListScreen.tsx
// import React, { useState, useEffect, useCallback } from "react";
// import Header from "../components/layout/Header";
// import FilterBar from "../components/patientlist/FilterBar";
// import PatientTable from "../components/patientlist/PatientTable";
// import Pagination from "../components/patientlist/Pagination";
// import { fetchPatients } from "../services/patientService";
// import { Patient, PatientStatus } from "../types";
// import { useAuth } from "../context/AuthContext"; // ✅ added
// import "../css/PatientListScreen.css";

// const ITEMS_PER_PAGE = 15;

// const PatientListScreen: React.FC = () => {
//   const { doctor } = useAuth(); // ✅ added

//   const [patients, setPatients] = useState<Patient[]>([]);
//   const [loading, setLoading] = useState<boolean>(true);
//   const [currentPage, setCurrentPage] = useState<number>(1);
//   const [totalPages, setTotalPages] = useState<number>(0);
//   const [filters, setFilters] = useState<{
//     emrNumber?: string;
//     screeningDate?: string;
//     status?: PatientStatus | "";
//   }>({});

//   const loadPatients = useCallback(async () => {
//     setLoading(true);
//     try {
//       // ✅ Role-based queue
//       // const response = await fetchPatients(doctor?.type);
//       const response = await fetchPatients();


//       setPatients(response.patients);
//       setTotalPages(response.totalPages);
//     } catch (error) {
//       console.error("Failed to fetch patients:", error);
//     } finally {
//       setLoading(false);
//     }
//   }, [doctor?.type]); // ✅ dependency added

// useEffect(() => {
//   loadPatients();

//   // const interval = setInterval(() => {
//   //   loadPatients();
//   // }, 2000);

//   // return () => clearInterval(interval);
// }, [loadPatients]);

//   const handlePageChange = (page: number) => {
//     setCurrentPage(page);
//   };

//   const handleFilterChange = (newFilters: {
//     emrNumber?: string;
//     screeningDate?: string;
//     status?: PatientStatus | "";
//   }) => {
//     setCurrentPage(1);
//     setFilters(newFilters);
//   };

//   return (
//     <div className="patient-list-page">
//       <main>
//         <FilterBar onFilterChange={handleFilterChange} />
//         <PatientTable
//           patients={patients}
//           loading={loading}
//           currentPage={currentPage}
//           itemsPerPage={ITEMS_PER_PAGE}
//         />
//         <Pagination
//           currentPage={currentPage}
//           totalPages={totalPages}
//           onPageChange={handlePageChange}
//         />
//       </main>
//     </div>
//   );
// };

// export default PatientListScreen;



import React, {
  useState,
  useEffect,
  useCallback,
} from "react";

import FilterBar from "../components/patientlist/FilterBar";
import PatientTable from "../components/patientlist/PatientTable";
import Pagination from "../components/patientlist/Pagination";

import { fetchPatients } from "../services/patientService";

import {
  Patient,
  PatientStatus,
  QueueClaimReview,
} from "../types";

import { useAuth } from "../context/AuthContext";

import "../css/PatientListScreen.css";

const ITEMS_PER_PAGE = 15;

const PatientListScreen: React.FC = () => {
  const {
    doctor,
    currentUser,
  } = useAuth();

  /**
   * IMPORTANT:
   *
   * doctorId must be the internal user-management Mongo/Object ID:
   *
   * currentUser.user._id
   *
   * NOT the Auth0 user ID.
   */
  const doctorId =
    currentUser?.user?._id || "";

  /**
   * The current-user response contains the doctor's
   * actual name inside user.raw.name.
   *
   * Fall back to the existing doctor object so the
   * existing application behaviour remains safe.
   */
  const doctorName =
    currentUser?.user?.raw?.name ||
    doctor?.name ||
    "";

  const [
    patients,
    setPatients,
  ] = useState<Patient[]>([]);

  const [
    myActiveReview,
    setMyActiveReview,
  ] = useState<QueueClaimReview | null>(null);

  const [
    loading,
    setLoading,
  ] = useState<boolean>(true);

  const [
    currentPage,
    setCurrentPage,
  ] = useState<number>(1);

  const [
    totalPages,
    setTotalPages,
  ] = useState<number>(0);

  const [
    filters,
    setFilters,
  ] = useState<{
    emrNumber?: string;
    screeningDate?: string;
    status?: PatientStatus | "";
  }>({});

  /**
   * Load queue data.
   *
   * showLoader controls whether the full table loader
   * should be displayed.
   *
   * Initial load:
   *   showLoader = true
   *
   * Polling refresh:
   *   showLoader = false
   *
   * This prevents the table from flashing every 5 seconds.
   */
  const loadPatients = useCallback(
    async (showLoader = false) => {
      if (!doctorId) {
        return;
      }

      if (showLoader) {
        setLoading(true);
      }

      try {
        const response =
          await fetchPatients(doctorId);

        setPatients(
          response.patients
        );

        setTotalPages(
          response.totalPages
        );

        setMyActiveReview(
          response.myActiveReview || null
        );
      } catch (error) {
        console.error(
          "Failed to fetch patients:",
          error
        );
      } finally {
        if (showLoader) {
          setLoading(false);
        }
      }
    },
    [doctorId]
  );

  /**
   * Initial queue load.
   */
  useEffect(() => {
    if (!doctorId) {
      return;
    }

    loadPatients(true);
  }, [
    doctorId,
    loadPatients,
  ]);

  /**
   * Refresh queue every 5 seconds.
   *
   * This allows:
   *
   * Doctor A claims Child 1
   *        ↓
   * Doctor B's queue refreshes
   *        ↓
   * Child 1 disappears from waiting
   *
   * And when a claim expires:
   *
   * Child becomes WAITING again
   *        ↓
   * queue refresh
   *        ↓
   * Child becomes available again
   */
  useEffect(() => {
    if (!doctorId) {
      return;
    }

    const intervalId =
      window.setInterval(() => {
        loadPatients(false);
      }, 5000);

    return () => {
      window.clearInterval(
        intervalId
      );
    };
  }, [
    doctorId,
    loadPatients,
  ]);

  const handlePageChange = (
    page: number
  ) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (
    newFilters: {
      emrNumber?: string;
      screeningDate?: string;
      status?: PatientStatus | "";
    }
  ) => {
    setCurrentPage(1);
    setFilters(newFilters);
  };

  return (
    <div className="patient-list-page">
      <main>
        <FilterBar
          onFilterChange={
            handleFilterChange
          }
        />

        <PatientTable
          patients={patients}
          loading={loading}
          currentPage={currentPage}
          itemsPerPage={
            ITEMS_PER_PAGE
          }
          myActiveReview={
            myActiveReview
          }
          doctorId={doctorId}
          doctorName={doctorName}
          onQueueRefresh={() =>
            loadPatients(false)
          }
        />

        <Pagination
          currentPage={
            currentPage
          }
          totalPages={
            totalPages
          }
          onPageChange={
            handlePageChange
          }
        />
      </main>
    </div>
  );
};

export default PatientListScreen;